import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Target, Zap, PenLine, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ConfirmDelete from "@/components/ConfirmDelete";

const METRICS = [
  { value: "hires", label: "New hires joined" },
  { value: "screened", label: "Candidates screened" },
  { value: "interviews", label: "Interviews conducted" },
  { value: "reviews", label: "Performance reviews completed" },
  { value: "compliance_done", label: "Compliance items closed" },
  { value: "docs_completed", label: "Employee documents uploaded" },
  { value: "exits_max", label: "Keep exits under limit" },
];

const DURATIONS = [7, 15, 30];

export default function Goals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ branches: [], departments: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [progressDraft, setProgressDraft] = useState({});
  // Filters
  const [fType, setFType] = useState("all");
  const [fDept, setFDept] = useState("All");
  const [fDuration, setFDuration] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (fFrom) p.set("from_date", fFrom);
    if (fTo) p.set("to_date", fTo);
    return api.get(`/goals${p.toString() ? `?${p}` : ""}`).then((r) => setItems(r.data)).catch(() => toast.error("Failed to load goals"));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fFrom, fTo]);
  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return items.filter((g) => {
      if (fType !== "all" && g.goal_type !== fType) return false;
      if (fDept !== "All" && g.department !== fDept) return false;
      if (fDuration !== "all" && String(g.duration_days) !== fDuration) return false;
      if (fStatus !== "all" && g.status !== fStatus) return false;
      if (fFrom && (g.end_date || "").slice(0, 10) < fFrom) return false;
      if (fTo && (g.start_date || "").slice(0, 10) > fTo) return false;
      return true;
    });
  }, [items, fType, fDept, fDuration, fStatus, fFrom, fTo]);

  const counts = useMemo(() => ({
    active: filtered.filter((g) => g.status === "Active").length,
    completed: filtered.filter((g) => g.status === "Completed").length,
    missed: filtered.filter((g) => g.status === "Missed").length,
  }), [filtered]);

  const openNew = () => {
    setForm({
      goal_type: "auto", duration_days: 15, department: "All",
      branch: user?.role === "branch_manager" && user?.branch ? user.branch : "All",
      start_date: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const openEdit = (g) => {
    setForm({
      id: g.id, title: g.title, description: g.description || "",
      goal_type: g.goal_type, metric: g.metric || "", target_value: g.target_value,
      department: g.department || "All", branch: g.branch || "All",
      duration_days: g.duration_days, start_date: (g.start_date || "").slice(0, 10),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const { id, ...body } = form;
      if (id) {
        await api.patch(`/goals/${id}`, body);
        toast.success("Goal updated");
      } else {
        await api.post("/goals", body);
        toast.success("Goal created");
      }
      setOpen(false);
      setForm({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/goals/${id}`);
      toast.success("Goal deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const saveProgress = async (g) => {
    const val = progressDraft[g.id];
    if (val === undefined) return;
    try {
      await api.patch(`/goals/${g.id}`, { manual_progress: Number(val) });
      toast.success("Progress updated");
      setProgressDraft((p) => { const n = { ...p }; delete n[g.id]; return n; });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const statusCls = (s) => ({ Active: "rag-amber", Completed: "rag-green", Missed: "rag-red" }[s] || "rag-amber");

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Goals &amp; Targets</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Department Goals</h1>
          <p className="text-slate-600 text-sm mt-1">Set 7 / 15 / 30-day goals — automatic goals track live data from your other tabs; manual goals you update yourself.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="add-goal-button">
          <Plus className="h-4 w-4 mr-1" /> New goal
        </Button>
      </div>

      {/* Summary */}
      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Active goals" value={counts.active} />
        <Stat label="Completed" value={counts.completed} tone="emerald" />
        <Stat label="Missed" value={counts.missed} tone="rose" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="goals-filters">
        <div className="w-36">
          <Label className="label-eyebrow">Type</Label>
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="filter-goal-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="auto">Automatic</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Label className="label-eyebrow">Department</Label>
          <Select value={fDept} onValueChange={setFDept}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="filter-goal-dept"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="All">All departments</SelectItem>
              {meta.departments?.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <Label className="label-eyebrow">Duration</Label>
          <Select value={fDuration} onValueChange={setFDuration}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="filter-goal-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              {DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-36">
          <Label className="label-eyebrow">Status</Label>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="filter-goal-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
              <SelectItem value="Missed">Missed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow">From</Label>
          <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="mt-1.5 h-9 w-40" data-testid="filter-goal-from" />
        </div>
        <div>
          <Label className="label-eyebrow">To</Label>
          <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="mt-1.5 h-9 w-40" data-testid="filter-goal-to" />
        </div>
        {(fType !== "all" || fDept !== "All" || fDuration !== "all" || fStatus !== "all" || fFrom || fTo) && (
          <Button variant="ghost" className="h-9 text-slate-500" onClick={() => { setFType("all"); setFDept("All"); setFDuration("all"); setFStatus("all"); setFFrom(""); setFTo(""); }} data-testid="clear-goal-filters">
            Clear filters
          </Button>
        )}
      </div>

      {/* Goal cards */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-14 text-center text-slate-500" data-testid="goals-empty">
          <Target className="h-8 w-8 mx-auto text-slate-300 mb-3" />
          No goals match. Create your first goal with &ldquo;New goal&rdquo;.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((g) => (
            <div key={g.id} className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col gap-3" data-testid={`goal-card-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {g.goal_type === "auto" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-emerald-50 text-[var(--yc-emerald)]">
                      <Zap className="h-3 w-3" /> Automatic
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-slate-100 text-slate-600">
                      <PenLine className="h-3 w-3" /> Manual
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-slate-100 text-slate-600">{g.duration_days}-day</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${statusCls(g.status)}`}>{g.status}</span>
                </div>
                {g.can_edit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => openEdit(g)} className="p-1.5 rounded-md text-slate-400 hover:text-[var(--yc-navy)] hover:bg-slate-100 transition-colors" title="Edit" data-testid={`edit-goal-${g.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <ConfirmDelete onConfirm={() => remove(g.id)} label={`goal "${g.title}"`} testId={`delete-goal-${g.id}`} />
                  </div>
                )}
              </div>

              <div>
                <div className="font-heading font-semibold text-[var(--yc-navy)]">{g.title}</div>
                {g.description && <div className="text-xs text-slate-500 mt-0.5">{g.description}</div>}
                <div className="text-xs text-slate-500 mt-1">
                  {g.metric_label ? `${g.metric_label} · ` : ""}{g.department} · {g.branch}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-slate-500">
                    {g.goal_type === "auto"
                      ? g.metric === "exits_max"
                        ? `${g.current_value} exit(s) / limit ${Math.round(g.target_value)}`
                        : `${g.current_value} / ${Math.round(g.target_value)}`
                      : `${Math.round(g.progress_pct)}%`}
                  </span>
                  <span className="font-mono text-slate-600">{Math.round(g.progress_pct)}%</span>
                </div>
                <Progress value={g.progress_pct} className="h-2" />
              </div>

              {g.goal_type === "manual" && g.can_edit && g.status === "Active" && (
                <div className="flex items-center gap-2">
                  <input
                    type="range" min={0} max={100} step={5}
                    value={progressDraft[g.id] ?? g.manual_progress ?? 0}
                    onChange={(e) => setProgressDraft((p) => ({ ...p, [g.id]: e.target.value }))}
                    className="flex-1 accent-[var(--yc-emerald)]"
                    data-testid={`progress-slider-${g.id}`}
                  />
                  {progressDraft[g.id] !== undefined && Number(progressDraft[g.id]) !== Number(g.manual_progress || 0) && (
                    <Button size="sm" className="h-7 text-xs bg-[var(--yc-emerald)] hover:bg-emerald-700" onClick={() => saveProgress(g)} data-testid={`save-progress-${g.id}`}>
                      Save {progressDraft[g.id]}%
                    </Button>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between text-[11px] text-slate-500 border-t border-slate-100 pt-2.5 mt-auto">
                <span className="inline-flex items-center gap-1">
                  <CalendarClock className="h-3.5 w-3.5" />
                  {(g.start_date || "").slice(0, 10)} → {(g.end_date || "").slice(0, 10)}
                </span>
                <span>{g.status === "Active" ? `${g.days_left}d left` : g.status} · by {g.created_by}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Edit goal" : "New goal"}</DialogTitle>
            <DialogDescription>Automatic goals track live data from your existing tabs; manual goals you update yourself.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="label-eyebrow">Goal title</Label>
              <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="e.g., Hire 3 tele-callers this fortnight" data-testid="goal-title-input" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Description (optional)</Label>
              <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Goal type</Label>
              <div className="flex gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, goal_type: "auto" })}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${form.goal_type === "auto" ? "border-[var(--yc-emerald)] bg-emerald-50 text-[var(--yc-emerald)]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  data-testid="goal-type-auto"
                >
                  <Zap className="h-3.5 w-3.5 inline mr-1" /> Automatic (data-linked)
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, goal_type: "manual" })}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${form.goal_type === "manual" ? "border-[var(--yc-navy)] bg-slate-50 text-[var(--yc-navy)]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  data-testid="goal-type-manual"
                >
                  <PenLine className="h-3.5 w-3.5 inline mr-1" /> Manual (write your own)
                </button>
              </div>
            </div>
            {form.goal_type === "auto" && (
              <>
                <div>
                  <Label className="label-eyebrow">Tracked metric</Label>
                  <Select value={form.metric || ""} onValueChange={(v) => setForm({ ...form, metric: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="goal-metric-select"><SelectValue placeholder="Choose metric" /></SelectTrigger>
                    <SelectContent>{METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow">{form.metric === "exits_max" ? "Max exits allowed" : "Target count"}</Label>
                  <Input type="number" min={1} value={form.target_value || ""} onChange={(e) => setForm({ ...form, target_value: Number(e.target.value) })} className="mt-1.5" data-testid="goal-target-input" />
                </div>
              </>
            )}
            <div>
              <Label className="label-eyebrow">Department</Label>
              <Select value={form.department || "All"} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger className="mt-1.5" data-testid="goal-dept-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="All">All departments</SelectItem>
                  {meta.departments?.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Branch</Label>
              <Select value={form.branch || "All"} onValueChange={(v) => setForm({ ...form, branch: v })} disabled={user?.role === "branch_manager"}>
                <SelectTrigger className="mt-1.5" data-testid="goal-branch-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All branches</SelectItem>
                  {meta.branches?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Duration</Label>
              <div className="flex gap-2 mt-1.5">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setForm({ ...form, duration_days: d })}
                    className={`flex-1 rounded-md border px-2 py-2 text-sm font-medium transition-colors ${form.duration_days === d ? "border-[var(--yc-navy)] bg-[var(--yc-navy)] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`goal-duration-${d}`}
                  >
                    {d} days
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Start date</Label>
              <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" data-testid="goal-start-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-goal-button">{form.id ? "Save changes" : "Create goal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone = "navy" }) {
  const cls = tone === "rose" ? "text-[var(--yc-rose)]" : tone === "emerald" ? "text-[var(--yc-emerald)]" : "text-[var(--yc-navy)]";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="label-eyebrow">{label}</div>
      <div className={`font-heading font-bold text-3xl mt-2 ${cls}`}>{value}</div>
    </div>
  );
}
